const COUNT_CONTAINER_SELECTORS = [
  ".product-count__text",
  ".mobile-facets__header-inner",
];
const COUNT_SPAN_SELECTOR = COUNT_CONTAINER_SELECTORS.map(
  (selector) => `${selector} .product-count__full`,
).join(", ");

let lastCount: number | null = null;
let mobileBound = false;
let reapplyObserver: MutationObserver | null = null;
let waitForRootObserver: MutationObserver | null = null;

function getSiteId(): string | null {
  const raw = document.body?.dataset?.ssSiteId;
  const trimmed = raw?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

function getCollectionHandle(): string | null {
  const raw = document.body?.dataset?.ssCollectionHandle;
  const trimmed = raw?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

function buildApiUrl(): string {
  const siteId = getSiteId();
  if (!siteId) {
    throw new Error(
      "SearchSpring siteId is missing from document.body.dataset.ssSiteId",
    );
  }
  const apiUrl = new URL(
    `https://${siteId}.a.searchspring.io/api/search/search.json`,
  );
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isSearchPage = pathSegments[0] === "search";

  apiUrl.searchParams.set("siteId", siteId);
  apiUrl.searchParams.set("resultsFormat", "native");
  apiUrl.searchParams.set("resultsPerPage", isSearchPage ? "48" : "1");
  apiUrl.searchParams.set("page", "1");

  const configuredCollectionHandle = getCollectionHandle();
  const collectionsIndex = pathSegments.indexOf("collections");
  const urlCollectionHandle =
    collectionsIndex >= 0 ? (pathSegments[collectionsIndex + 1] ?? "") : "";

  let collectionHandleToUse =
    urlCollectionHandle || configuredCollectionHandle || "";
  if (urlCollectionHandle === "vendors" && configuredCollectionHandle) {
    collectionHandleToUse = configuredCollectionHandle;
  }
  if (!isSearchPage && collectionHandleToUse) {
    apiUrl.searchParams.set(
      "bgfilter.collection_handle",
      collectionHandleToUse,
    );
  }

  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("q");
  if (searchQuery) apiUrl.searchParams.set("q", searchQuery);

  location.hash
    .replace(/^#/, "")
    .split("/")
    .forEach((segment) => {
      if (!segment.startsWith("filter:")) return;
      const keyValue = segment.slice(7);
      const colonIndex = keyValue.indexOf(":");
      if (colonIndex <= 0 || colonIndex >= keyValue.length - 1) return;
      const key = keyValue.slice(0, colonIndex);
      const rawValue = keyValue.slice(colonIndex + 1);
      if (key === "ss_price" && rawValue.includes(":")) {
        const [low, high] = rawValue.split(":");
        if (low !== undefined && low !== "")
          apiUrl.searchParams.set("filter.ss_price.low", low);
        if (high !== undefined && high !== "")
          apiUrl.searchParams.set("filter.ss_price.high", high);
      } else {
        let value = rawValue;
        if (value.includes("$25")) {
          try {
            value = decodeURIComponent(value.replace(/\$25/g, "%"));
          } catch (error) {
            console.warn(
              "SearchSpring product count: failed to decode filter value",
              value,
              error,
            );
          }
        }
        // allow multiple values for the same filter key (OR logic)
        apiUrl.searchParams.append(`filter.${key}`, value);
      }
    });
  searchParams.forEach((value, key) => {
    if (key.startsWith("filter.")) apiUrl.searchParams.append(key, value);
  });
  return apiUrl.toString();
}

async function getCount(): Promise<number> {
  const response = await fetch(buildApiUrl());
  if (!response.ok) {
    throw new Error(
      `SearchSpring product count failed: ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  const total = data?.pagination?.totalResults;
  if (typeof total !== "number" || !Number.isFinite(total)) {
    throw new Error("SearchSpring returned invalid totalResults");
  }
  return Math.floor(total);
}

function applyCount(count: number): void {
  const countText = `${count} products`;
  COUNT_CONTAINER_SELECTORS.forEach((containerSelector) =>
    document
      .querySelectorAll<HTMLElement>(containerSelector)
      .forEach((container) => {
        if (!container.querySelector(".product-count__full")) {
          const countSpan = document.createElement("span");
          countSpan.className = "product-count__full";
          container.appendChild(countSpan);
        }
      }),
  );
  document
    .querySelectorAll<HTMLElement>(COUNT_SPAN_SELECTOR)
    .forEach((span) => (span.textContent = countText));
}

function bindMobile(): void {
  if (mobileBound) return;
  const button = document.querySelector<HTMLElement>(".ss__slideout__button");
  if (!button) return;
  mobileBound = true;
  button.addEventListener("click", () => {
    if (lastCount !== null) applyCount(lastCount);
  });
}

let reapplyScheduled = false;

function scheduleReapply(): void {
  if (reapplyScheduled) return;
  reapplyScheduled = true;
  requestAnimationFrame(() => {
    reapplyScheduled = false;
    if (lastCount !== null) applyCount(lastCount);
    bindMobile();
  });
}

function ensureReapplyOnDomChange(): void {
  if (reapplyObserver) return;

  const searchspringRoot = document.getElementById("searchspring-content");
  if (searchspringRoot) {
    reapplyObserver = new MutationObserver(() => scheduleReapply());
    reapplyObserver.observe(searchspringRoot, {
      childList: true,
      subtree: true,
    });
    return;
  }

  if (waitForRootObserver) return;
  waitForRootObserver = new MutationObserver(() => {
    if (!document.getElementById("searchspring-content")) return;
    waitForRootObserver?.disconnect();
    waitForRootObserver = null;
    ensureReapplyOnDomChange();
  });
  waitForRootObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function run(): void {
  getCount()
    .then((count) => {
      lastCount = count;
      applyCount(count);
      ensureReapplyOnDomChange();
    })
    .catch((error) => {
      console.warn("SearchSpring product count failed", error);
    });
}

function wrapHistoryMethod(methodName: "pushState" | "replaceState") {
  const originalMethod = history[methodName];
  return function (this: History, ...args: Parameters<History["pushState"]>) {
    const result = originalMethod.apply(this, args);
    run();
    return result;
  };
}

function init(): void {
  if (!getSiteId()) return;
  if (document.body?.dataset?.ssProductCountSource === "theme") return;
  history.pushState = wrapHistoryMethod("pushState");
  history.replaceState = wrapHistoryMethod("replaceState");
  addEventListener("popstate", run);
  addEventListener("hashchange", run);
  bindMobile();
  run();
}

addEventListener("DOMContentLoaded", init);
